/**
 * EquipeManager — Componente genérico de gestão de equipe.
 * Renderiza Hero + Toolbar + Lista + Wizard + Ficha para qualquer perfil.
 * Configurado via EquipeConfig.
 * @internal true
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Users, Search, UserPlus, Eye, Pencil, UserX, X,
  ChevronLeft, ChevronRight, Mail, Phone, Check,
  Clock, AlertTriangle, Ban, ShieldOff, Copy,
  CheckCircle2, HelpCircle, RefreshCw, LayoutGrid, List,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Modal } from '../ui/Modal';
import { ConfirmModal } from '../ui/ConfirmModal';
import { PaginationFooter } from '../ui/DataTable';
import { formatPhone } from '../../utils/cpfUtils';
import type { Usuario } from '../../types/models';
import { InviteResultDialog } from '../invite';
import type { EquipeConfig, EquipeRole } from './EquipeConfig';
import { useAuthStore } from '../../store/authStore';
import {
  ALL_PERMISSIONS,
  getPermissionsByRole,
  hasPermission,
  togglePermissionWithCoherence,
  type PermissionKey,
  type PermissionMap,
} from '../../auth/permissions';
import './equipe-manager.css';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface InviteResult {
  activationLink: string;
  whatsappLink: string | null;
  emailSent: boolean;
  userName: string;
  codigoDisplay?: string;
}

interface EquipeManagerProps {
  config: EquipeConfig;
}

// ═══════════════════════════════════════════════════════════════
// STATUS HELPERS
// ═══════════════════════════════════════════════════════════════

const STATUS_MAP: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  ATIVA: { label: 'Ativa', color: '#059669', bg: '#ecfdf5', icon: CheckCircle2 },
  PENDENTE: { label: 'Pendente', color: '#d97706', bg: '#fffbeb', icon: Clock },
  ATIVANDO: { label: 'Ativando', color: '#2563eb', bg: '#eff6ff', icon: RefreshCw },
  SUSPENSA: { label: 'Suspensa', color: '#dc2626', bg: '#fef2f2', icon: Ban },
  BLOQUEADA: { label: 'Bloqueada', color: '#6b7280', bg: '#f9fafb', icon: ShieldOff },
  FALHOU: { label: 'Falhou', color: '#dc2626', bg: '#fef2f2', icon: AlertTriangle },
};

const getStatusInfo = (statusConta?: string) => {
  return STATUS_MAP[statusConta || ''] || STATUS_MAP.PENDENTE;
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function EquipeManager({ config }: EquipeManagerProps) {
  const user = useAuthStore((s) => s.user);
  const canManage = hasPermission(user, 'equipe.manage');

  // ── Data State ──
  const [membros, setMembros] = useState<Usuario[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── UI State ──
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'lista'>('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  // ── Wizard State ──
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);

  // ── Form State ──
  const [form, setForm] = useState({
    nome: '',
    email: '',
    telefone: '',
    cargo: '',
    permissaoPreset: 'PADRAO' as 'PADRAO' | 'PERSONALIZADO',
    permissoes: {} as PermissionMap,
  });

  // ── Other ──
  const [deleteConfirm, setDeleteConfirm] = useState<Usuario | null>(null);
  const [statusConfirm, setStatusConfirm] = useState<{ membro: Usuario; novoStatus: string } | null>(null);
  const [reenviando, setReenviando] = useState(false);

  // ── Fetch Data ──
  const fetchMembros = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get(config.api.listar);
      setMembros(res.data || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao carregar equipe');
    } finally {
      setIsLoading(false);
    }
  }, [config.api.listar]);

  useEffect(() => {
    void fetchMembros();
  }, [fetchMembros]);

  // ── Derived Data ──
  const filteredMembros = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return membros.filter((u: any) => {
      if (filterRole && u.cargo !== filterRole) return false;
      if (filterStatus && u.statusConta !== filterStatus) return false;
      if (q) {
        const text = [u.nome, u.email, u.cargo, u.statusConta].filter(Boolean).join(' ').toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [membros, searchQuery, filterRole, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filteredMembros.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedMembros = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredMembros.slice(start, start + pageSize);
  }, [filteredMembros, safePage, pageSize]);

  const stats = useMemo(() => ({
    total: membros.length,
    ativos: membros.filter((u) => u.ativo).length,
  }), [membros]);

  // ── Wizard Helpers ──
  const defaultRole = config.roles.find((r) => r.isDefault) || config.roles[0];

  const resetForm = () => {
    setForm({
      nome: '',
      email: '',
      telefone: '',
      cargo: defaultRole?.id || '',
      permissaoPreset: 'PADRAO',
      permissoes: getPermissionsByRole(config.perfil, defaultRole?.id || '') || ({} as PermissionMap),
    });
    setStep(1);
    setEditing(null);
    setInviteResult(null);
  };

  const openNew = () => {
    resetForm();
    setWizardOpen(true);
  };

  const openEdit = (membro: Usuario) => {
    setEditing(membro);
    setForm({
      nome: membro.nome || '',
      email: membro.email || '',
      telefone: (membro as any).telefone || '',
      cargo: (membro as any).cargo || config.roles[0]?.id || '',
      permissaoPreset: 'PADRAO',
      permissoes: (membro as any)?.preferencias?.permissoes || getPermissionsByRole(config.perfil, (membro as any).cargo || ''),
    });
    setStep(1);
    setWizardOpen(true);
  };

  const canProceedStep1 = () =>
    form.nome.trim().length > 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const handleCloseInvite = () => {
    setInviteResult(null);
    setWizardOpen(false);
    resetForm();
  };

  const steps = editing
    ? [{ n: 1, label: 'Dados' }, ...(config.features.permissions ? [{ n: 2, label: 'Permissões' }] : [])]
    : [
        { n: 1, label: 'Identificação' },
        { n: 2, label: 'Função' },
        ...(config.features.permissions ? [{ n: 3, label: 'Permissões' }] : []),
      ];

  // ── CRUD ──
  const handleSave = async () => {
    if (!form.nome.trim() || !form.email.trim()) {
      toast.error('Nome e email são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(config.api.update(editing.id), {
          nome: form.nome,
          email: form.email,
          telefone: form.telefone?.replace(/\D/g, '') || undefined,
          cargo: form.cargo || undefined,
          ...(config.features.permissions && form.permissaoPreset === 'PERSONALIZADO'
            ? { permissoes: form.permissoes, permissaoPreset: 'PERSONALIZADO' }
            : { permissaoPreset: 'PADRAO' }),
        });
        toast.success('Membro atualizado!');
        setWizardOpen(false);
        resetForm();
        void fetchMembros();
      } else {
        const res = await api.post(config.api.convidar, {
          nome: form.nome,
          email: form.email,
          telefone: form.telefone?.replace(/\D/g, '') || undefined,
          cargo: form.cargo || undefined,
          ...(config.features.permissions && form.permissaoPreset === 'PERSONALIZADO'
            ? { permissoes: form.permissoes, permissaoPreset: 'PERSONALIZADO' }
            : {}),
        });
        setInviteResult({
          activationLink: res.data.activationLink,
          whatsappLink: null,
          emailSent: res.data.emailEnviado,
          userName: form.nome,
          codigoDisplay: res.data.codigoDisplay,
        });
        setWizardOpen(false);
        void fetchMembros();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(config.api.remove(deleteConfirm.id));
      toast.success('Membro removido');
      void fetchMembros();
    } catch {
      toast.error('Erro ao remover');
    }
    setDeleteConfirm(null);
  };

  const confirmStatus = async () => {
    if (!statusConfirm) return;
    try {
      await api.patch(config.api.status(statusConfirm.membro.id), {
        statusConta: statusConfirm.novoStatus,
      });
      toast.success(`Status alterado para ${statusConfirm.novoStatus}`);
      void fetchMembros();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao alterar status');
    }
    setStatusConfirm(null);
  };

  const handleReenviar = async (membro: Usuario) => {
    setReenviando(true);
    try {
      const res = await api.post(config.api.reenviar(membro.id));
      toast.success('Novo link de ativação gerado com sucesso!');
      setForm({
        nome: membro.nome,
        email: membro.email,
        telefone: (membro as any).telefone || '',
        cargo: (membro as any).cargo || '',
        permissaoPreset: 'PADRAO',
        permissoes: (membro as any)?.preferencias?.permissoes || ({} as PermissionMap),
      });
      setInviteResult({
        activationLink: res.data.activationLink,
        whatsappLink: null,
        emailSent: res.data.emailEnviado,
        userName: membro.nome,
        codigoDisplay: res.data.codigoDisplay,
      });
      void fetchMembros();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erro ao reenviar convite');
    } finally {
      setReenviando(false);
    }
  };

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : 'Nunca';

  const getRoleLabel = (cargo?: string) => {
    const role = config.roles.find((r) => r.id === cargo);
    return role?.label || cargo || 'Membro';
  };

  const getRoleColor = (cargo?: string) => {
    const role = config.roles.find((r) => r.id === cargo);
    return role?.color || '#6b7280';
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="admin-page animate-fadeIn eqm-page">
      {/* ═══ HERO STRIP — Padrão Banco de Mídias ═══ */}
      <div className="eqm-hero">
        <div className="eqm-hero-circle c1" />
        <div className="eqm-hero-circle c2" />
        <div className="eqm-hero-content">
          <div style={{ minWidth: 0, flexShrink: 1 }}>
            <div className="mdm-hero-badge">
              <Users size={10} /> {config.labels.title}
            </div>
            <h1 className="mdm-hero-title">
              {config.labels.title}
            </h1>
            <p className="mdm-hero-subtitle">
              {stats.total} membro(s) cadastrado(s) · {stats.ativos} ativo(s) com acesso liberado à {config.labels.scope}.
            </p>
          </div>
          {canManage && (
            <button className="eqm-new-btn" onClick={openNew}>
              <UserPlus size={13} /> <span>{config.labels.inviteButton}</span>
            </button>
          )}
        </div>
      </div>

      {/* ═══ DATA CONTAINER COM TOOLBAR PADRONIZADA ═══ */}
      <div className="eqm-table-container">
        <div className="eqm-toolbar">
          <div className="eqm-toolbar-left">
            <div className="eqm-search-wrap">
              <Search size={14} className="eqm-search-icon" />
              <input
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="Buscar por nome, email, cargo..."
                className="eqm-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                  className="eqm-search-clear"
                  title="Limpar busca"
                >
                  <X size={13} />
                </button>
              )}
            </div>

            {config.roles && config.roles.length > 1 && (
              <select
                value={filterRole}
                onChange={(e) => { setFilterRole(e.target.value); setCurrentPage(1); }}
                className="eqm-toolbar-select"
              >
                <option value="">Todos os Cargos</option>
                {config.roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.label}</option>
                ))}
              </select>
            )}

            {config.features.statusControl && (
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                className="eqm-toolbar-select"
              >
                <option value="">Todos os Status</option>
                <option value="ATIVA">Ativos</option>
                <option value="PENDENTE">Pendentes</option>
                <option value="SUSPENSA">Suspensos</option>
              </select>
            )}

            {canManage && (
              <button className="eqm-new-btn-toolbar" onClick={openNew}>
                <UserPlus size={13} /> {config.labels.inviteButton}
              </button>
            )}
          </div>

          <div className="eqm-toolbar-right">
            <span className="eqm-toolbar-count">{filteredMembros.length} registro(s)</span>

            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="eqm-toolbar-select"
              style={{ width: 105 }}
            >
              {[6, 12, 24, 48].map((n) => (
                <option key={n} value={n}>{n}/página</option>
              ))}
            </select>

            <div className="eqm-view-modes">
              <button
                type="button"
                className={`eqm-view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                onClick={() => setViewMode('cards')}
                title="Visualização em Cards"
              >
                <LayoutGrid size={14} />
              </button>
              <button
                type="button"
                className={`eqm-view-btn ${viewMode === 'lista' ? 'active' : ''}`}
                onClick={() => setViewMode('lista')}
                title="Visualização em Lista"
              >
                <List size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* ═══ MODO CARDS (PADRÃO) ═══ */}
        {viewMode === 'cards' ? (
          <div className="eqm-cards-grid">
            {isLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', gridColumn: '1 / -1' }}>Carregando membros...</div>
            ) : paginatedMembros.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', gridColumn: '1 / -1' }}>
                {searchQuery ? 'Nenhum resultado encontrado' : config.labels.emptyState}
              </div>
            ) : paginatedMembros.map((u: any) => {
              const si = getStatusInfo(u.statusConta);
              const StatusIcon = si.icon;
              const roleColor = getRoleColor(u.cargo);
              const isSelf = !!user && (user.id === u.id || (!!user.email && !!u.email && user.email.toLowerCase() === u.email.toLowerCase()));
              return (
                <div key={u.id} className="eqm-card">
                  {/* Top Bar: Cargo + Status */}
                  <div className="eqm-card-top">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="eqm-card-role" style={{ background: `${roleColor}18`, color: roleColor, borderColor: `${roleColor}30` }}>
                        {getRoleLabel(u.cargo)}
                      </span>
                      {isSelf && (
                        <span style={{ fontSize: '0.62rem', fontWeight: 800, padding: '2px 6px', borderRadius: 6, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                          Você
                        </span>
                      )}
                    </div>
                    <span className="eqm-status-badge" style={{ '--badge-color': si.color, '--badge-bg': si.bg } as React.CSSProperties}>
                      <StatusIcon size={11} />
                      {si.label}
                    </span>
                  </div>

                  {/* Avatar & Identidade */}
                  <div className="eqm-card-body">
                    {u.fotoUrl ? (
                      <img src={u.fotoUrl} alt="" className="eqm-card-avatar-img" />
                    ) : (
                      <div className="eqm-card-avatar" style={{ background: `linear-gradient(135deg, ${roleColor}, #1e3a5f)` }}>
                        {u.nome.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <h4 className="eqm-card-name" title={u.nome}>{u.nome}</h4>
                    <span className="eqm-card-email" title={u.email}>
                      <Mail size={11} /> {u.email}
                    </span>
                    {u.telefone && (
                      <span className="eqm-card-phone">
                        <Phone size={11} /> {formatPhone(u.telefone)}
                      </span>
                    )}
                    <span className="eqm-card-access">
                      <Clock size={11} /> Acesso: {formatDate(u.ultimoLogin)}
                    </span>
                  </div>

                  {/* Ações Inferiores */}
                  <div className="eqm-card-actions">
                    <button
                      className="eqm-card-btn"
                      onClick={() => canManage && openEdit(u)}
                      disabled={!canManage}
                      style={!canManage ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                      title={canManage ? 'Editar' : 'Apenas visualização'}
                    >
                      <Pencil size={12} /> Editar
                    </button>
                    {!isSelf && (u.statusConta === 'PENDENTE' || u.statusConta === 'FALHOU') && (
                      <button
                        className="eqm-card-btn"
                        onClick={() => canManage && void handleReenviar(u)}
                        title={canManage ? 'Reenviar convite' : 'Apenas visualização'}
                        disabled={!canManage || reenviando}
                        style={!canManage ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                      >
                        <RefreshCw size={12} /> Reenviar
                      </button>
                    )}
                    {!isSelf && config.features.statusControl && u.statusConta === 'ATIVA' && (
                      <button
                        className="eqm-card-btn warning"
                        onClick={() => canManage && setStatusConfirm({ membro: u, novoStatus: 'SUSPENSA' })}
                        title={canManage ? 'Suspender' : 'Apenas visualização'}
                        disabled={!canManage}
                        style={!canManage ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                      >
                        <Ban size={12} /> Suspender
                      </button>
                    )}
                    {!isSelf && canManage && (
                      <button className="eqm-card-btn danger" onClick={() => setDeleteConfirm(u)} title="Remover">
                        <UserX size={12} /> Remover
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ═══ MODO LISTA / TABELA ═══ */
          <div>
            <div className="eqm-list-header eqm-grid">
              <span>Membro</span>
              <span>Cargo</span>
              <span className="eqm-hide-sm">Status</span>
              <span className="eqm-hide-sm">Último Acesso</span>
              <span style={{ textAlign: 'center' }}>Ações</span>
            </div>

            <div className="eqm-list-scroll">
              {isLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
              ) : paginatedMembros.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                  {searchQuery ? 'Nenhum resultado encontrado' : config.labels.emptyState}
                </div>
              ) : paginatedMembros.map((u: any) => {
                const si = getStatusInfo(u.statusConta);
                const StatusIcon = si.icon;
                const isSelf = !!user && (user.id === u.id || (!!user.email && !!u.email && user.email.toLowerCase() === u.email.toLowerCase()));
                return (
                  <div key={u.id} className="eqm-row eqm-grid">
                    {/* Membro */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                      {u.fotoUrl ? (
                        <img src={u.fotoUrl} alt="" className="eqm-avatar-img" />
                      ) : (
                        <div className="eqm-avatar" style={{ background: getRoleColor(u.cargo) }}>
                          {u.nome.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '.82rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.nome}</span>
                          {isSelf && (
                            <span style={{ fontSize: '0.6rem', fontWeight: 800, padding: '1px 5px', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>
                              Você
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                          <Mail size={10} /> {u.email}
                        </div>
                      </div>
                    </div>

                    {/* Cargo */}
                    <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                      {getRoleLabel(u.cargo)}
                    </div>

                    {/* Status */}
                    <div className="eqm-hide-sm">
                      <span className="eqm-status-badge" style={{ '--badge-color': si.color, '--badge-bg': si.bg } as React.CSSProperties}>
                        <StatusIcon size={12} />
                        {si.label}
                      </span>
                    </div>

                    {/* Último Acesso */}
                    <div className="eqm-hide-sm" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>
                      {formatDate(u.ultimoLogin)}
                    </div>

                    {/* Ações */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
                      <button
                        className="eqm-action-btn"
                        onClick={() => canManage && openEdit(u)}
                        disabled={!canManage}
                        style={!canManage ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                        title={canManage ? 'Editar' : 'Apenas visualização'}
                      >
                        <Pencil size={14} />
                      </button>
                      {!isSelf && (u.statusConta === 'PENDENTE' || u.statusConta === 'FALHOU') && (
                        <button
                          className="eqm-action-btn"
                          onClick={() => canManage && void handleReenviar(u)}
                          title={canManage ? 'Reenviar convite' : 'Apenas visualização'}
                          disabled={!canManage || reenviando}
                          style={!canManage ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                        >
                          <RefreshCw size={14} />
                        </button>
                      )}
                      {!isSelf && config.features.statusControl && u.statusConta === 'ATIVA' && (
                        <button
                          className="eqm-action-btn warning"
                          onClick={() => canManage && setStatusConfirm({ membro: u, novoStatus: 'SUSPENSA' })}
                          title={canManage ? 'Suspender' : 'Apenas visualização'}
                          disabled={!canManage}
                          style={!canManage ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                        >
                          <Ban size={14} />
                        </button>
                      )}
                      {!isSelf && canManage && (
                        <button className="eqm-action-btn danger" onClick={() => setDeleteConfirm(u)} title="Remover">
                          <UserX size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* PaginationFooter padrão */}
        <PaginationFooter
          pageIndex={safePage - 1}
          pageSize={pageSize}
          totalItems={filteredMembros.length}
          onPageChange={(p) => setCurrentPage(p + 1)}
        />
      </div>

      {/* ═══ WIZARD MODAL ═══ */}
      <Modal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        title=""
        size="lg"
        contentClassName="eqm-wizard-modal"
      >
        <div className="eqm-wizard">
          {/* Banner */}
          <div className="eqm-wiz-banner" style={{ '--eqm-hero': config.heroColor } as React.CSSProperties}>
            <div className="eqm-wiz-banner-circle" />
            <button onClick={() => setWizardOpen(false)} className="eqm-wiz-close"><X size={18} /></button>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <h2 className="eqm-wiz-title">{editing ? 'Editar Membro' : config.labels.inviteButton}</h2>
              <p className="eqm-wiz-subtitle">
                {editing ? 'Atualize os dados do membro.' : config.labels.subtitle}
              </p>
            </div>
            {/* Stepper */}
            <div className="eqm-wiz-stepper">
              {steps.map((s, i) => {
                const isCurrent = step === s.n;
                const isDone = step > s.n;
                return (
                  <div key={s.n} className="eqm-wiz-step-item">
                    <div className={`eqm-wiz-step-circle ${isCurrent ? 'current' : isDone ? 'done' : 'pending'}`}>
                      {isDone ? <Check size={14} /> : s.n}
                    </div>
                    <span className={`eqm-wiz-step-label ${isCurrent ? 'current' : ''}`}>{s.label}</span>
                    {i < steps.length - 1 && <div className={`eqm-wiz-step-line ${isDone ? 'done' : ''}`} />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div className="eqm-wiz-body">
            {/* Step 1: Identificação */}
            {step === 1 && (
              <div className="eqm-wiz-fields">
                <div className="eqm-wiz-field-group">
                  <label className="eqm-wiz-label">Nome completo *</label>
                  <input
                    className="eqm-wiz-input"
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    placeholder="Ex: Maria da Silva"
                  />
                </div>
                <div className="eqm-wiz-row">
                  <div className="eqm-wiz-field-group" style={{ flex: 1 }}>
                    <label className="eqm-wiz-label">Email institucional *</label>
                    <div style={{ position: 'relative' }}>
                      <Mail size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                      <input
                        className="eqm-wiz-input"
                        style={{ paddingLeft: 34 }}
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="email@instituicao.gov.br"
                      />
                    </div>
                  </div>
                  <div className="eqm-wiz-field-group" style={{ flex: 1 }}>
                    <label className="eqm-wiz-label">Telefone / WhatsApp</label>
                    <div style={{ position: 'relative' }}>
                      <Phone size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                      <input
                        className="eqm-wiz-input"
                        style={{ paddingLeft: 34 }}
                        value={form.telefone}
                        onChange={(e) => setForm({ ...form, telefone: formatPhone(e.target.value) })}
                        placeholder="(31) 99999-9999"
                        maxLength={15}
                      />
                    </div>
                  </div>
                </div>
                <div className="eqm-wiz-info-card" style={{ marginTop: 4 }}>
                  <Mail size={16} style={{ color: config.heroColor, flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.78rem', color: 'var(--text-main)' }}>Sem senha necessária</div>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1 }}>
                      O novo membro receberá um link de ativação para definir sua própria senha com segurança.
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Função / Cargo */}
            {step === 2 && !editing && (
              <div className="eqm-wiz-fields">
                <label className="eqm-wiz-label" style={{ marginBottom: 4 }}>Cargo / Função *</label>
                <p style={{ fontSize: '.74rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
                  Selecione o nível de acesso deste membro ao painel.
                </p>
                <div className="eqm-perm-grid">
                  {config.roles.map((role) => {
                    const selected = form.cargo === role.id;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        className={`eqm-perm-card ${selected ? 'selected' : ''}`}
                        onClick={() => setForm({
                          ...form,
                          cargo: role.id,
                          permissoes: form.permissaoPreset === 'PADRAO'
                            ? getPermissionsByRole(config.perfil, role.id)
                            : form.permissoes,
                        })}
                        style={{ '--perm-color': role.color } as React.CSSProperties}
                      >
                        <div className="eqm-perm-header">
                          <div className="eqm-perm-icon" style={{ background: `${role.color}15`, color: role.color }}>
                            <role.icon size={18} />
                          </div>
                          <div className={`eqm-perm-radio ${selected ? 'checked' : ''}`}>
                            {selected && <Check size={12} />}
                          </div>
                        </div>
                        <div className="eqm-perm-label">{role.label}</div>
                        {role.description && <div className="eqm-perm-desc">{role.description}</div>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 2 (edit) or Step 3 (new): Permissões */}
            {config.features.permissions && (
              (editing && step === 2) || (!editing && step === 3)
            ) && (
              <div className="eqm-wiz-fields">
                <label className="eqm-wiz-label" style={{ marginBottom: 8 }}>Nível de Permissão</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <button
                    type="button"
                    className={`eqm-preset-btn ${form.permissaoPreset === 'PADRAO' ? 'active' : ''}`}
                    onClick={() => setForm({
                      ...form,
                      permissaoPreset: 'PADRAO',
                      permissoes: getPermissionsByRole(config.perfil, form.cargo),
                    })}
                  >
                    Padrão do cargo
                  </button>
                  <button
                    type="button"
                    className={`eqm-preset-btn ${form.permissaoPreset === 'PERSONALIZADO' ? 'active' : ''}`}
                    onClick={() => setForm({ ...form, permissaoPreset: 'PERSONALIZADO' })}
                  >
                    Personalizado
                  </button>
                </div>
                {form.permissaoPreset === 'PERSONALIZADO' && (
                  <div className="eqm-perm-groups">
                    {config.permissionGroups.map((group) => (
                      <div key={group.title} className="eqm-perm-group">
                        <div className="eqm-perm-group-title">{group.title}</div>
                        {group.items.map((item) => (
                          <label key={item.key} className="eqm-perm-check">
                            <input
                              type="checkbox"
                              checked={!!form.permissoes[item.key]}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  permissoes: togglePermissionWithCoherence(form.permissoes, item.key, e.target.checked),
                                })
                              }
                            />
                            <span>{item.label}</span>
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="eqm-wiz-footer">
            <button
              className="eqm-wiz-btn secondary"
              onClick={() => (step > 1 ? setStep(step - 1) : setWizardOpen(false))}
            >
              {step > 1 ? '← Voltar' : 'Cancelar'}
            </button>
            {step < steps.length ? (
              <button
                className="eqm-wiz-btn primary"
                onClick={() => {
                  if (step === 1 && !canProceedStep1()) {
                    toast.error('Preencha nome e email válido');
                    return;
                  }
                  setStep(step + 1);
                }}
              >
                Próximo →
              </button>
            ) : (
              <button
                className="eqm-wiz-btn primary"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Enviando...' : editing ? 'Salvar Alterações' : 'Enviar Convite'}
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* ═══ RESULTADO DE CONVITE / REENVIO (MODAL COM QR CODE & LINK) ═══ */}
      {inviteResult && (
        <InviteResultDialog
          open={true}
          nome={inviteResult.userName}
          email={form.email}
          cargo={form.cargo}
          perfil={config.perfil}
          telefone={form.telefone}
          activationLink={inviteResult.activationLink}
          whatsappLink={inviteResult.whatsappLink}
          emailSent={inviteResult.emailSent}
          onClose={handleCloseInvite}
        />
      )}

      {/* ═══ DELETE CONFIRM ═══ */}
      <ConfirmModal
        open={!!deleteConfirm}
        title="Remover membro"
        message={deleteConfirm ? `Remover "${deleteConfirm.nome}" da ${config.labels.scope}? ${
          ['PENDENTE', 'ATIVANDO'].includes((deleteConfirm as any).statusConta || '')
            ? 'O convite será cancelado permanentemente.'
            : 'O acesso será revogado (o registro é preservado).'
        }` : ''}
        confirmLabel="Remover"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDelete()}
      />

      {/* ═══ STATUS CONFIRM ═══ */}
      <ConfirmModal
        open={!!statusConfirm}
        title="Alterar status"
        message={statusConfirm ? `Alterar status de "${statusConfirm.membro.nome}" para ${statusConfirm.novoStatus}?` : ''}
        confirmLabel="Confirmar"
        onCancel={() => setStatusConfirm(null)}
        onConfirm={() => void confirmStatus()}
      />
    </div>
  );
}
